import React, { useEffect, useMemo, ReactNode, useState } from "react";
import { Dimmer, Loader } from "semantic-ui-react";

// Serializer
import DoctorRecommendationTemplateSerializer from "issara-sdk/types/DoctorRecommendationTemplateSerializer_core";
import CheckupReportSerializer from "issara-sdk/types/CheckupReportSerializer_core";

// Components
import CardHealthRecommendationUX from "./CardHealthRecommendationUX";
import CardMedicalReportPdfViewer from "./CardMedicalReportPdfViewer";
import DoctorNoteTextEditor from "./DoctorNoteTextEditor";
import ModDoctorNoteTemplate from "./ModDoctorNoteTemplate";
import RenderHealthRecommendationActionButton from "./RenderHealthRecommendationActionButton";

// Common
import SnackMessage from "react-lib/apps/common/SnackMessage";

// Interface
import {
  PickedState,
  RunSequence,
  SetProp,
  State,
  OnEvent,
  MasterOptions,
  HealthRecommendationData,
  ActionReport,
  CARD_NAME,
} from "./sequence/HealthRecommendation";

// Type
type CardHealthRecommendationProps = {
  onEvent: OnEvent;
  setProp: SetProp;
  runSequence: RunSequence;
  HealthRecommendationSequence: State["HealthRecommendationSequence"];
  masterOptions: MasterOptions;
  languageUX: "en" | "th";
} & Pick<
  PickedState,
  "buttonLoadCheck" | "errorMessage" | "successMessage" | "selectedEncounter" | "selectedEmr" | "loadingStatus"
>;

export type TemplateItem = DoctorRecommendationTemplateSerializer & {
  isEditing?: boolean;
};

type CheckupReportFields = keyof CheckupReportSerializer;

type HealthRecommendationRootFields = Exclude<
  keyof HealthRecommendationData,
  "checkup_report_data"
>;

type HealthRecommendationFieldName = HealthRecommendationRootFields | CheckupReportFields;

/*================================ CONSTANTS ==============================*/

const LANGUAGE_OPTIONS = [
  { key: "th", value: "th", text: "TH" },
  { key: "en", value: "en", text: "EN" },
];

/*================================ UTIL FUNCS ==============================*/

const MappedOptions = <T,>(
  source: T[] | undefined,
  mapper: (item: T) => { key: any; value: any; text: ReactNode },
  fallback: { key: any; value: any; text: ReactNode }[] = []
) => {
  return useMemo(() => {
    if (!source?.length) return fallback;
    return source.map(mapper);
  }, [source, fallback, mapper]);
};

/*================================ MAIN COMPONENT ==============================*/

const CardHealthRecommendation = (props: CardHealthRecommendationProps) => {
  const [openNoteTemplate, setOpenNoteTemplate] = useState<boolean>(false);
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<
    DoctorRecommendationTemplateSerializer[]
  >([]);
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  const doctorNoteTemplateList = useMemo(
    () => props.HealthRecommendationSequence?.commonOptions?.doctorNoteTemplateList || [],
    [props.HealthRecommendationSequence?.commonOptions?.doctorNoteTemplateList]
  );

  useEffect(() => {
    props.runSequence({
      sequence: "HealthRecommendation",
      restart: true,
    });
  }, []);

  useEffect(() => {
    setTemplates(doctorNoteTemplateList);
  }, [doctorNoteTemplateList]);

  const handleOpenNoteTemplate = () => {
    setTemplates([...doctorNoteTemplateList]);
    setSelectedTemplates([]);
    setSearchKeyword("");
    setOpenNoteTemplate(true);
  };

  const handleCloseNoteTemplate = () => {
    handleSearchDoctorNoteTemplate("");

    setOpenNoteTemplate(false);
    setTemplates(doctorNoteTemplateList || []);
    setTemplates([]);
    setSelectedTemplates([]);
    setSearchKeyword("");
  };

  const checkupReportData = useMemo(
    () => props.HealthRecommendationSequence?.data?.checkup_report_data || {},
    [props.HealthRecommendationSequence?.data?.checkup_report_data]
  );

  const healthRecommendationData = useMemo(
    () => props.HealthRecommendationSequence?.data || {},
    [props.HealthRecommendationSequence?.data]
  );

  const pdfData = useMemo(
    () => props.HealthRecommendationSequence?.pdfData || {},
    [props.HealthRecommendationSequence?.pdfData]
  );

  const encounterList = MappedOptions(
    props.HealthRecommendationSequence?.commonOptions?.encounterList,
    (item) => ({
      key: item.id,
      value: item.id,
      text: (
        <label>
          <span style={{ fontWeight: "bold" }}>{item.full_number}</span>
          &nbsp;{item.created}
        </label>
      ),
    })
  );

  const checkupReportList = MappedOptions(
    props.HealthRecommendationSequence?.commonOptions?.checkupReportList,
    (item) => ({
      key: item.id,
      value: item.id,
      text: `Checkup Report: ${item.id}`,
    })
  );

  const pdfB64Data = useMemo(() => {
    if (pdfData?.checkup_report && checkupReportData?.id) {
      return `data:application/pdf;base64,${pdfData?.checkup_report}#toolbar=0`;
    }
    return "about:blank";
  }, [pdfData?.checkup_report, checkupReportData?.id]);

  const handleChangeData = (
    _event: any,
    data: { name: HealthRecommendationFieldName; value: any }
  ) => {
    const { name, value } = data;

    if ((["id", "doctor_recommendation"] as CheckupReportFields[]).includes(name as any)) {
      const data = props.HealthRecommendationSequence?.data;
      if (!data) return;

      props.setProp(`HealthRecommendationSequence.data.checkup_report_data.${name}` as any, value);
    } else {
      props.setProp(`HealthRecommendationSequence.data.${name}` as any, value);
    }

    if (name === "encounter") {
      // Clear Checkup Report and PDF
      props.setProp(`HealthRecommendationSequence.data.checkup_report_data`, {});
      props.setProp(`HealthRecommendationSequence.pdfData`, {
        checkup_report: undefined,
        approve_report: undefined,
      });

      props.runSequence({
        sequence: "HealthRecommendation",
        action: "GET_CHECKUP_LIST",
        encounter: value,
      });
    }

    if (name === "language") {
      props.runSequence({
        sequence: "HealthRecommendation",
        action: "FETCH_ALL_PDF",
      });
    }

    if (name === "id") {
      const checkupData: HealthRecommendationData = {
        language: healthRecommendationData?.language,
        encounter: healthRecommendationData?.encounter,
      };

      props.runSequence({
        sequence: "HealthRecommendation",
        action: "GET_CHECKUP_REPORT",
        checkupReportId: value,
        checkupData,
      });
    }

    if (name === "doctor_recommendation") {
      props.runSequence({
        sequence: "HealthRecommendation",
        action: "FETCH_DOCTOR_NOTE",
      });
    }
  };

  const handlePrintReport = () => {
    props.runSequence({
      sequence: "HealthRecommendation",
      action: "PRINT_REPORT",
      checkupReport: pdfData?.checkup_report || "",
    });
  };

  const handleSaveReport = (saveAction: ActionReport) => {
    const latestData = props.HealthRecommendationSequence?.data as HealthRecommendationData;

    props.runSequence({
      sequence: "HealthRecommendation",
      action: "SAVE_REPORT",
      saveAction: saveAction,
      data: latestData,
    });
  };

  const handleSearchDoctorNoteTemplate = (name: string) => {
    props.runSequence({
      sequence: "HealthRecommendation",
      action: "GET_TEMPLATE",
      name,
    });
  };

  const handleCreateDoctorNoteTemplate = (createdData: DoctorRecommendationTemplateSerializer) => {
    props.runSequence({
      sequence: "HealthRecommendation",
      action: "CREATE_TEMPLATE",
      data: {
        name: createdData.name,
        detail: createdData.detail,
      },
    });
  };

  const handleEditDoctorNoteTemplate = (editedData: DoctorRecommendationTemplateSerializer) => {
    props.runSequence({
      sequence: "HealthRecommendation",
      action: "EDIT_TEMPLATE",
      templateId: editedData?.id || 0,
      data: editedData,
    });
  };

  const handleDeleteDoctorNoteTemplate = (templateId: number) => {
    props.runSequence({
      sequence: "HealthRecommendation",
      action: "DELETE_TEMPLATE",
      templateId,
    });
  };

  const handleSelectTemplate = (templates: DoctorRecommendationTemplateSerializer[]) => {
    const combinedDetail =
      templates.length > 1
        ? templates.map((t) => t.detail).join("<br/>")
        : templates[0]?.detail || "";

    handleChangeData(null, { name: "doctor_recommendation", value: combinedDetail });

    props.runSequence({
      sequence: "HealthRecommendation",
      action: "FETCH_DOCTOR_NOTE",
    });
  };

  return (
    <>
      <SnackMessage
        // func
        onEvent={props.onEvent}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_NAME}`, null);
          props.setProp(`successMessage.${CARD_NAME}`, null);
        }}
        error={props.errorMessage?.[CARD_NAME]}
        success={props.successMessage?.[CARD_NAME]}
      />

      <CardHealthRecommendationUX
        // * --- Loader
        onLoading={
          <Dimmer active={props.loadingStatus?.[CARD_NAME]} inverted>
            <Loader inverted>Loading</Loader>
          </Dimmer> 
        }
        // * --- Options
        languageOptions={LANGUAGE_OPTIONS}
        encounterOptions={encounterList}
        checkupReportOptions={checkupReportList}
        isDisabled={!checkupReportData?.id}
        // * --- Data
        data={props.HealthRecommendationSequence?.data}
        // * --- Callback
        onChangeData={handleChangeData}
        onOpenNoteTemplate={handleOpenNoteTemplate}
        // * --- Component
        pdfPreview={
          <CardMedicalReportPdfViewer
            src={pdfB64Data}
            id="CardHealthRecommendation-Iframe-PDFViewer"
            style={{ height: "calc(90vh - 10rem)" }}
          />
        }
        doctorNoteTextEditor={
          <DoctorNoteTextEditor
            key={checkupReportData?.id}
            disable={!!checkupReportData?.approved || !checkupReportData?.id}
            name="doctor_recommendation"
            content={
              props.HealthRecommendationSequence?.data?.checkup_report_data
                ?.doctor_recommendation || ""
            }
            onChange={handleChangeData}
          />
        }
        // * --- Button
        renderActionButton={
          <RenderHealthRecommendationActionButton
            // func
            setProp={props.setProp}
            onPrintReport={handlePrintReport}
            onSaveReport={handleSaveReport}
            // data
            checkupReportData={checkupReportData}
            buttonLoadCheck={props.buttonLoadCheck}
          />
        }
      />

      <ModDoctorNoteTemplate
        // Common
        setProp={props.setProp}
        onOpen={openNoteTemplate}
        onClose={handleCloseNoteTemplate}
        onSearch={handleSearchDoctorNoteTemplate}
        onCreate={handleCreateDoctorNoteTemplate}
        onEdit={handleEditDoctorNoteTemplate}
        onDelete={handleDeleteDoctorNoteTemplate}
        onSelectTemplate={handleSelectTemplate}
        // Data
        templates={templates}
        selectedTemplates={selectedTemplates}
        searchKeyword={searchKeyword}
        // Setter
        onChangeSearch={setSearchKeyword}
        onChangeTemplates={setTemplates}
        onChangeSelectedTemplates={setSelectedTemplates}
        // Other
        buttonLoadCheck={props.buttonLoadCheck}
      />
    </>
  );
};

export default CardHealthRecommendation;
